// Vulnerability page
// -------------------
// Tailwind/shadcn migration. Layout matches WebApplication and Dashboard:
// max-w-6xl single column on a gray-50 background, white Cards with soft
// borders, text-sm body / text-base section headings / text-2xl page title.
//
// Risk rating is read-only by design — see the conversation with the user
// for the rationale (audit integrity, risk-accepted as the legitimate channel
// for downgrades). The status dropdown mirrors the SingleVulnRow pattern.
//
// Optional fields (screenshot, steps to reproduce, recommendations) are
// rendered conditionally so the page looks clean even when those fields are
// missing on older test data.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";

import {
  EMPTY_STRING,
  SINGLE_WEBAPP_PAGE,
} from "@/constants/definitions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import HttpExchangeCard from "./HttpExchangeCard";
import HelpTip from "@/components/common/HelpTip";
import EvidenceImage from "@/components/common/EvidenceImage";
import Skeleton from "@/components/common/Skeleton";
import { useDelayedFlag } from "@/utils/useDelayedFlag";
import { fetchVulnerabilityById } from "@/services/dataListOperations.service";
import CognitoLoginClass from "@/models/cognitoLogin";
import UserClass from "@/models/user";
import VulnerabilityClass from "@/models/vulnerability";
import { eventBus, VULN_STATUS_UPDATE } from "@/services/eventBus.service";
import WebApplicationClass from "@/models/webApplication";
import TestResultsClass from "@/models/testResult";
import { withRouter, WithRouterProps } from "@/router/withRouter";
import { formatDateWithLocale } from "@/utils/date";
import { referenceLinkIsNavigable } from "@/utils/urlSafety";
import {
  getSeverityTileStyle,
  getSeverityLabel,
  getSeverityPillStyle,
} from "@/utils/severityStyles";
import { generatePath } from "react-router-dom";

// ----------------------------------------
//   Constants
// ----------------------------------------

// How long a detail fetch may run before the page admits to waiting.
// Below this the work is imperceptible and a placeholder would read as a
// flicker; above it, silence is what makes an empty page confusing.
// See utils/useDelayedFlag.ts for the reasoning in full.
const LOADING_INDICATOR_DELAY_MS = 250;

// ----------------------------------------
//   Props
// ----------------------------------------

interface VulnerabilityProps extends WithRouterProps {
  user: UserClass;
  listOfWebapps: WebApplicationClass[];
  webappid: string;
  cognitologin: CognitoLoginClass;
}

// ----------------------------------------
//   Component
// ----------------------------------------

function Vulnerability({
  listOfWebapps,
  webappid,
  cognitologin,
  params,
  navigate,
  location,
  searchParams,
  setSearchParams,
}: VulnerabilityProps) {
  // ----------------------------------------
  //   State
  // ----------------------------------------

  const [vuln, setVuln] = useState<VulnerabilityClass>(new VulnerabilityClass());
  // True while the detail fetch for the CURRENT vuln is in flight. Drives
  // the loading treatment; deliberately not set for the background test
  // hydrate, which the user is never waiting on.
  const [isFetchingDetail, setIsFetchingDetail] = useState<boolean>(false);
  // Set when the detail fetch failed or came back empty. Kept separate
  // from "not loading" because the two must not look the same: falling
  // back to the cards' empty states would tell the user this finding has
  // no reproduction steps, when the truth is we couldn't read them.
  const [detailLoadFailed, setDetailLoadFailed] = useState<boolean>(false);
  // Bumped after any in-place model mutation to force a re-render. The
  // models are mutable instances, so setVuln alone can't be relied on —
  // React bails out when the reference is unchanged. Same convention as
  // the WebApplication page.
  const [refreshKey, setRefreshKey] = useState<number>(0);
  // Evidence screenshots are presigned with a 15-minute expiry (see
  // EvidenceImage). Budget exactly ONE re-query per finding: enough to recover
  // links that died while the finding sat open, bounded so a genuinely missing
  // screenshot cannot drive an unending stream of queries.
  const [hasSpentEvidenceRefresh, setHasSpentEvidenceRefresh] = useState<boolean>(false);
  // Bumped when a refresh settles, either way. Tells the images that their turn
  // is over, so one whose src did not change can stop waiting for a retry.
  const [evidenceRefreshAttempts, setEvidenceRefreshAttempts] = useState<number>(0);

  const currentVulnId = searchParams.get("vulnid");

  // The vuln whose detail we most recently asked for. Prev/Next can
  // outrun a slow fetch, so every response is checked against this before
  // it is allowed to land.
  const requestedVulnIdRef = useRef<string | null>(null);
  // The vuln id whose detail fetch has already been started. Stops a
  // second request for the same finding when the load effect re-runs —
  // which it now does whenever the app list resolves late. Cleared on
  // failure so Try again can have another go.
  const detailFetchStartedForRef = useRef<string | null>(null);
  // The vuln id whose detail actually landed. Distinct from the ref
  // above, which is also true mid-flight: this one answers "is what we
  // are showing already the full record?", and stops a re-run from
  // downgrading the page back to the slim row it started from.
  const detailLoadedForRef = useRef<string | null>(null);
  // The test id whose background hydrate has already been started. Same
  // reasoning as above.
  const testHydrateStartedForRef = useRef<string | null>(null);
  // Cleared on unmount so a late response can't setState on a dead
  // component.
  const isMountedRef = useRef<boolean>(true);

  // ----------------------------------------
  //   Data lookup
  // ----------------------------------------

  // Stable stand-ins for "not found yet". Returning a fresh instance from
  // the lookups below would hand every render a new identity, which would
  // re-trigger every effect and memo keyed on them.
  const placeholderWebappRef = useRef<WebApplicationClass>(new WebApplicationClass());
  const placeholderTestResultRef = useRef<TestResultsClass>(new TestResultsClass());

  // Resolved per render rather than frozen at mount. On a hard refresh or
  // a bookmarked link the app list is still loading when this page first
  // paints, so a mount-time snapshot stayed empty for the life of the
  // page — which is why the toolbar read "Back to application" and
  // Previous/Next never appeared. Re-resolving means both fill in the
  // moment the list arrives.
  const webapp = useMemo(
    () =>
      listOfWebapps.find((candidate) => candidate.id === params?.webappid) ??
      placeholderWebappRef.current,
    [listOfWebapps, params?.webappid]
  );

  // refreshKey is a dependency because webapp.tests is mutated in place —
  // a test backfilled after this page mounted has no identity change to
  // key off.
  const testResult = useMemo(
    () =>
      webapp.tests.find((candidate) => candidate.id === params?.testid) ??
      placeholderTestResultRef.current,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [webapp, params?.testid, refreshKey]
  );

  // ----------------------------------------
  //   Vuln list for prev/next
  // ----------------------------------------

  // getDisplayVulns() rather than .vulns: on an in-flight run it unions
  // the findings streamed in live, which is what the table the user
  // clicked from was showing. Sourcing prev/next from a different list
  // than the table would skip findings.
  //
  // Recomputed on refreshKey because the underlying arrays are mutated in
  // place by the model — there is no stable identity to key off.
  const displayVulns = useMemo(
    () => testResult.getDisplayVulns?.() ?? testResult.vulns ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testResult, refreshKey]
  );

  const vulnIndex = useMemo(
    () => displayVulns.findIndex((candidate) => candidate.id === currentVulnId),
    [displayVulns, currentVulnId]
  );

  // ----------------------------------------
  //   Load vuln from URL
  // ----------------------------------------

  // Three steps, ordered so the user sees something real as early as
  // possible and is never blocked on data they aren't reading yet.
  //
  //   1. Paint from memory. Anything the table already had — risk, title,
  //      reference, dates — is on the model, so the header and
  //      Information card can render immediately.
  //   2. Fetch THIS vuln's detail by id. One record, one DynamoDB
  //      GetItem. The page used to hydrate the whole test for this, which
  //      pulls every finding in the run with its sub-findings and HTTP
  //      exchange bodies — that is where the multi-second wait came from.
  //   3. Backfill the rest of the run in the background, purely so
  //      Previous/Next know what else exists. Never gates a render.
  const loadVuln = useCallback(async (vulnid: string | null): Promise<void> => {
    if (!vulnid || !testResult) return;

    // Step 1 — whatever is already known. Skipped when the record on
    // screen is already the full one and the model's copy isn't: on a
    // live run the model's copy is the slim streamed row, and a re-run
    // adopting it would strip the detail back off the page.
    const knownVuln = (testResult.getDisplayVulns?.() ?? testResult.vulns ?? [])
      .find((candidate) => candidate.id === vulnid);
    const showingFullDetail = detailLoadedForRef.current === vulnid;
    if (knownVuln && (knownVuln.fullDetailLoaded || !showingFullDetail)) {
      setVuln(knownVuln);
    }

    // Step 3 — kicked off before the await below so it overlaps with the
    // detail fetch rather than queueing behind it.
    if (
      testResult.id &&
      !testResult.fullResultLoaded &&
      testHydrateStartedForRef.current !== testResult.id
    ) {
      testHydrateStartedForRef.current = testResult.id;
      testResult
        .getFullTestResult(testResult.id)
        .then(() => {
          if (isMountedRef.current) setRefreshKey((key) => key + 1);
        })
        .catch(() => {
          // Prev/Next stay limited to what's already loaded. The detail
          // the user actually came for is unaffected.
          testHydrateStartedForRef.current = null;
        });
    }

    // Step 2 — the detail itself, skipped when we already hold a full
    // record (arriving from a hydrated test, or coming back to a vuln)
    // or when a request for it is already out.
    if (knownVuln?.fullDetailLoaded) return;
    if (detailFetchStartedForRef.current === vulnid) return;
    detailFetchStartedForRef.current = vulnid;

    setIsFetchingDetail(true);
    setDetailLoadFailed(false);
    try {
      const detailPayload = await fetchVulnerabilityById(vulnid);
      // Stale response — the user has moved on to another finding.
      if (requestedVulnIdRef.current !== vulnid || !isMountedRef.current) return;

      if (detailPayload) {
        // Upgrade the model's own instance where there is one, so this
        // vuln is instant next time and every other surface sees the
        // richer record too.
        const targetVuln = knownVuln ?? new VulnerabilityClass();
        targetVuln.setVulnerability(detailPayload);
        detailLoadedForRef.current = vulnid;
        setVuln(targetVuln);
        setRefreshKey((key) => key + 1);
      } else {
        // No record came back — deleted, or the resolver denied it.
        detailFetchStartedForRef.current = null;
        setDetailLoadFailed(true);
      }
    } catch (detailError) {
      // Leaves whatever step 1 painted in place — a slim row beats an
      // empty page — and says so rather than implying the finding is bare.
      console.error(`Failed to load vulnerability ${vulnid}:`, detailError);
      detailFetchStartedForRef.current = null;
      if (requestedVulnIdRef.current === vulnid && isMountedRef.current) {
        setDetailLoadFailed(true);
      }
    } finally {
      if (requestedVulnIdRef.current === vulnid && isMountedRef.current) {
        setIsFetchingDetail(false);
      }
    }
  }, [testResult]);

  // ----------------------------------------
  //   Lifecycle
  // ----------------------------------------

  useEffect(() => {
    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.className = "root";
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Runs on mount and on every ?vulnid= change, including the in-place
  // replace() that Previous/Next performs.
  useEffect(() => {
    requestedVulnIdRef.current = currentVulnId;
    loadVuln(currentVulnId);
  }, [currentVulnId, loadVuln]);

  // Each finding gets its own refresh budget — moving to another vuln arrives
  // with freshly signed URLs, and a failure there deserves its own retry.
  useEffect(() => {
    setHasSpentEvidenceRefresh(false);
    setEvidenceRefreshAttempts(0);
  }, [currentVulnId]);

  // An evidence screenshot failed to load. The overwhelmingly likely cause is an
  // expired presigned URL rather than a missing object, so re-run the query to
  // mint fresh signatures; the new src flows down and the image retries itself.
  // Re-queries the whole record because `render` and `additionalRenders` are
  // presigned as ordinary fields of it — there is no narrower endpoint, and this
  // runs at most once per finding.
  const handleEvidenceUrlExpired = useCallback((): void => {
    if (hasSpentEvidenceRefresh) return;
    setHasSpentEvidenceRefresh(true);

    const vulnid = requestedVulnIdRef.current;
    if (!vulnid) {
      setEvidenceRefreshAttempts((attempts) => attempts + 1);
      return;
    }

    fetchVulnerabilityById(vulnid)
      .then((detailPayload) => {
        // Stale response — the user has moved on to another finding.
        if (!isMountedRef.current || requestedVulnIdRef.current !== vulnid) return;
        if (detailPayload) {
          // Mutates the model instance in place, so every other surface sees the
          // refreshed URLs too; refreshKey is what actually repaints.
          vuln.setVulnerability(detailPayload);
          setRefreshKey((key) => key + 1);
        }
      })
      .catch((refreshError) => {
        // Non-fatal: the image settles into its "failed to load" state below.
        console.error(`Failed to refresh evidence URLs for ${vulnid}:`, refreshError);
      })
      .finally(() => {
        if (isMountedRef.current) {
          setEvidenceRefreshAttempts((attempts) => attempts + 1);
        }
      });
  }, [hasSpentEvidenceRefresh, vuln]);

  // Live vuln-status subscription — when the backend publishes a status /
  // riskRating / dateFixed change for the vuln this page is showing, update
  // the local view. The model's setVulnerability already ran in the
  // WebApplicationClass handler; we just need to surface the mutated fields
  // by re-finding the vuln in testResult.vulns and replacing the state ref.
  useEffect(() => {
    const handleVulnStatusUpdate = (payload?: { vulnid?: string }) => {
      if (!payload?.vulnid) return;
      if (payload.vulnid !== searchParams.get("vulnid")) return;
      const updatedVuln = testResult.vulns?.find(
        (candidate) => candidate.id === payload.vulnid
      );
      if (updatedVuln) setVuln(updatedVuln);
    };
    eventBus.on(VULN_STATUS_UPDATE, handleVulnStatusUpdate);
    return () => {
      eventBus.off(VULN_STATUS_UPDATE, handleVulnStatusUpdate);
    };
  }, [searchParams, testResult]);

  // ----------------------------------------
  //   Navigation
  // ----------------------------------------

  // Changing ?vulnid= is the single trigger for a move: the effect above
  // watches it and runs the same load sequence as a fresh arrival, so
  // Previous/Next behave identically to clicking a row in the table.
  const navigateToVulnIndex = useCallback((newIndex: number): void => {
    const newVuln = displayVulns[newIndex];
    if (!newVuln) return;

    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set("vulnid", newVuln.id);
    navigate(`${location.pathname}?${urlSearchParams.toString()}`, { replace: true });
  }, [displayVulns, location, navigate]);

  const handleNavigatePrev = useCallback((): void => {
    const total = displayVulns.length;
    if (total === 0) return;
    // A vuln that isn't in the list yet (index -1) steps to the last
    // entry going back, and to the first going forward.
    const newIndex = vulnIndex - 1 < 0 ? total - 1 : vulnIndex - 1;
    navigateToVulnIndex(newIndex);
  }, [vulnIndex, displayVulns, navigateToVulnIndex]);

  const handleNavigateNext = useCallback((): void => {
    const total = displayVulns.length;
    if (total === 0) return;
    const newIndex = vulnIndex + 1 >= total ? 0 : vulnIndex + 1;
    navigateToVulnIndex(newIndex);
  }, [vulnIndex, displayVulns, navigateToVulnIndex]);

  const handleRetryDetail = useCallback((): void => {
    loadVuln(currentVulnId);
  }, [loadVuln, currentVulnId]);

  const handleClose = useCallback((): void => {
    navigate(generatePath(SINGLE_WEBAPP_PAGE, { webappid: webapp.id }));
  }, [navigate, webapp.id]);

  // ----------------------------------------
  //   Status update
  // ----------------------------------------

  // Status mutation removed for v1: vuln status is no longer user-mutable
  // from the UI. The badge renders the underlying vuln.vulnStatus as a
  // read-only label.

  // ----------------------------------------
  //   Render
  // ----------------------------------------

  const totalVulns = displayVulns.length;
  const severityLabel = getSeverityLabel(vuln.riskRating);
  const severityBlock = getSeverityTileStyle(vuln.riskRating);

  // ---- Loading treatment ----
  //
  // Two distinct questions, because the answers drive different parts of
  // the page:
  //
  //   isAwaitingDetail — is the detail still coming? While true, the
  //     Findings / Steps / Recommendations cards must NOT render. Their
  //     empty states say "No reproduction steps recorded for this
  //     vulnerability", which is a definitive statement about the finding
  //     and reads as fact right up until the content replaces it. Saying
  //     nothing is honest; saying "none" is not.
  //
  //   isAwaitingHeader — do we know anything at all about this finding?
  //     False when the user came from a table that already had the row
  //     (risk, title, reference, dates all render immediately, and only
  //     the detail below is pending). True on a cold arrival — a
  //     bookmarked link, or the dashboard where the run was never loaded.
  //
  // Both loading treatments are delay-gated, so the common fast path
  // shows no loading affordance whatsoever.
  const isAwaitingDetail = isFetchingDetail && !vuln.fullDetailLoaded;
  const isAwaitingHeader = isAwaitingDetail && !vuln.riskRating && !vuln.title && !vuln.vulnName;
  const showDetailSkeleton = useDelayedFlag(isAwaitingDetail, LOADING_INDICATOR_DELAY_MS);
  const showHeaderSkeleton = useDelayedFlag(isAwaitingHeader, LOADING_INDICATOR_DELAY_MS);
  // The detail didn't arrive and isn't coming without another attempt.
  const isDetailUnavailable = detailLoadFailed && !vuln.fullDetailLoaded;
  // The real cards render only when we can vouch for what they say.
  const showDetailCards = !isAwaitingDetail && !isDetailUnavailable;

  const hasDetails = Array.isArray(vuln.details) && vuln.details.length > 0;
  // SubFindings became the BE's primary "what was wrong" container.
  // Every post-refactor vuln carries at least one (single-finding
  // pentesters auto-derive one; multi-finding pentesters populate
  // explicitly). Legacy records pre-refactor land here with
  // subFindings === [], in which case we fall through to rendering
  // the parent `details` inline in the Information card.
  const hasSubFindings =
    Array.isArray(vuln.subFindings) && vuln.subFindings.length > 0;
  const hasSteps = Array.isArray(vuln.stepsToReproduce) && vuln.stepsToReproduce.length > 0;
  // Filter out the fauxdata generator's placeholder string so test data
  // surfaces an empty state instead of the literal "This is a faux
  // recommendation for testing purposes". Real recommendations from the
  // pentest engine will pass through unchanged.
  const realRecommendations = Array.isArray(vuln.recommendations)
    ? vuln.recommendations.filter(
        (rec) => rec && rec.trim() !== "This is a faux recommendation for testing purposes"
      )
    : [];
  const hasRecommendations = realRecommendations.length > 0;
  const hasScreenshot = !!vuln.render;

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col gap-6">
        {/* Top toolbar: back link + prev/next + close */}
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {webapp.name || "application"}
          </button>

          <div className="flex items-center gap-2">
            {totalVulns > 1 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNavigatePrev}
                  className="gap-1.5"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNavigateNext}
                  className="gap-1.5"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              className="gap-1.5"
            >
              <X className="h-3.5 w-3.5" />
              Close
            </Button>
          </div>
        </div>

        {/* Header card — prominent severity block on the left, title and
            meta on the right. Mirrors the TestDetails "Security Status"
            layout so the visual treatment is consistent across pages. */}
        <Card>
          <div className="flex items-stretch gap-6 px-6 py-5">
            {/* Severity block — TestDetails-style colored box.
                self-center + matched w/h locks it as a square regardless
                of how tall the meta column next to it ends up. */}
            <div
              className={`flex flex-col items-center justify-center rounded-xl px-2 w-28 h-28 self-center shrink-0 ${severityBlock.bg}`}
            >
              <p className={`text-2xl font-bold tabular-nums leading-none ${severityBlock.numberText}`}>
                {severityLabel}
              </p>
              <p className={`text-[11px] font-medium mt-1.5 uppercase tracking-wider ${severityBlock.labelText}`}>
                Risk
              </p>
            </div>

            {/* Title + meta. On a cold arrival the title is the one thing
                the user is looking for, so it gets a placeholder of its
                own rather than the "Unnamed vulnerability" fallback —
                which is a real state for legacy records and mustn't be
                borrowed to mean "still loading". */}
            <div className="flex-1 min-w-0 flex flex-col gap-3 justify-center">
              <div className="flex items-center gap-2 flex-wrap">
                {vuln.reference && (
                  <span className="text-xs text-gray-400">{vuln.reference}</span>
                )}
              </div>

              {showHeaderSkeleton ? (
                <Skeleton className="h-7 w-3/4 max-w-md" />
              ) : (
                <h1 className="text-2xl font-semibold text-gray-900">
                  {vuln.title || vuln.vulnName || "Unnamed vulnerability"}
                </h1>
              )}

              <div className="space-y-1.5 text-sm text-gray-500">
                <div>
                  <span className="text-gray-400">App:</span>{" "}
                  <span className="text-gray-700">{webapp.name || "—"}</span>
                </div>
                {showHeaderSkeleton && <Skeleton className="h-4 w-56" />}
                {vuln.vulnLocation && (
                  <div>
                    <span className="text-gray-400">Location:</span>{" "}
                    <span className="text-gray-700 break-all">{vuln.vulnLocation}</span>
                  </div>
                )}
                {/* Status row dropped in v1 — every vuln reaching this
                    page is Open by definition (observed in the latest
                    completed test), so a "Status: Open" line was pure
                    redundancy. The underlying vuln.vulnStatus field
                    remains on the model for legacy readback paths. */}
              </div>
            </div>
          </div>
        </Card>

        {/* Information card — built from vuln.details + standard fields */}
        <Card>
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Information</h2>
          </div>
          <CardContent className="p-6">
            {showHeaderSkeleton && (
              // Cold arrival: even First Found / Last Seen are unknown, and
              // rendering them as "—" would assert the finding has no
              // dates rather than that we haven't read them yet.
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {[0, 1, 2, 3].map((placeholderIndex) => (
                  <div key={placeholderIndex} className="flex flex-col gap-1.5">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                ))}
              </div>
            )}
            <dl
              className={`grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm ${
                isAwaitingHeader ? "hidden" : ""
              }`}
            >
              {vuln.pageUrl && (
                <div>
                  <dt className="text-gray-400 mb-0.5">Page URL</dt>
                  <dd className="text-gray-700 break-all">{vuln.pageUrl}</dd>
                </div>
              )}
              {vuln.vulnCategory && (
                <div>
                  <dt className="text-gray-400 mb-0.5">Category</dt>
                  <dd className="text-gray-700">{vuln.vulnCategory}</dd>
                </div>
              )}
              {/* First Found / Last Seen pair — matches the vuln table
                  columns on the WebApplication and Dashboard pages.
                  dateFirstFound is preserved from the earliest test in
                  which this vuln hash was observed; dateLastSeen is the
                  most recent completed test it was observed in. Showing
                  both makes carry-forward across tests legible to the
                  user (a vuln found weeks ago but still present in the
                  latest test reads as "First Found: weeks ago, Last
                  Seen: today"). */}
              <div>
                <dt className="text-gray-400 mb-0.5">First Found</dt>
                <dd className="text-gray-700">
                  {vuln.dateFirstFound
                    ? formatDateWithLocale(vuln.dateFirstFound)
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-400 mb-0.5">Last Seen</dt>
                <dd className="text-gray-700">
                  {vuln.dateLastSeen
                    ? formatDateWithLocale(vuln.dateLastSeen)
                    : "—"}
                </dd>
              </div>
              {/* Requires authentication — only shown when the backend has
                  reported a value (true or false). null means the field
                  isn't present in this payload (older test data). */}
              {vuln.requiresAuthentication !== null && (
                <div>
                  <dt className="text-gray-400 mb-0.5">Requires authentication</dt>
                  <dd className="text-gray-700">
                    {vuln.requiresAuthentication ? "Yes" : "No"}
                  </dd>
                </div>
              )}
              {/* Legacy details — rendered ONLY when subFindings is
                  empty (pre-refactor records). Once subFindings is
                  populated, the same information moves into the
                  Findings card below where the 2-column flow can't
                  jumble multi-finding sequences. */}
              {!hasSubFindings && hasDetails &&
                vuln.details
                  // The pentest engine still emits "Requires Authentication"
                  // as a detail row for some vuln types (XSS, etc) for
                  // backwards compatibility, but it's now also surfaced as
                  // a top-level field above. Suppress the duplicate row.
                  .filter(
                    (detail) =>
                      detail.label?.toLowerCase().trim() !== "requires authentication"
                  )
                  .map((detail, detailIndex) => (
                    <div key={`${detail.label}-${detailIndex}`}>
                      <dt className="text-gray-400 mb-0.5">{detail.label}</dt>
                      <dd className="text-gray-700 break-words">{detail.value}</dd>
                    </div>
                  ))}
            </dl>
          </CardContent>
        </Card>

        {/* Detail placeholder — stands in for the Findings, Steps to
            Reproduce and Recommendations cards while their content is on
            its way.

            One placeholder for the three of them, not three separate
            ones: the user is waiting on a single fetch, so a single
            explanation is honest and three would read as three separate
            stalls. It only appears once the fetch has outlived
            LOADING_INDICATOR_DELAY_MS — on the common path (a hydrated
            test, or a fetch that beats the threshold) it never renders at
            all, which is what keeps the page from feeling like it
            spinners at every click. */}
        {showDetailSkeleton && (
          <Card role="status" aria-busy="true" aria-live="polite">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-500">
                Loading details…
              </h2>
            </div>
            <CardContent className="p-6 flex flex-col gap-6">
              {/* Shaped like a sub-finding: heading, prose, evidence block. */}
              <div className="flex flex-col gap-2">
                <Skeleton className="h-5 w-1/2 max-w-xs" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-11/12" />
                <Skeleton className="h-4 w-2/3" />
              </div>
              <Skeleton className="h-40 w-full" />
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detail unavailable — the fetch failed or the record came back
            empty. Says so plainly and offers the retry, rather than
            falling through to cards whose empty states would report the
            finding as having no steps and no recommendations. */}
        {isDetailUnavailable && (
          <Card>
            <CardContent className="p-6 flex flex-col items-center gap-3 text-center">
              <p className="text-sm text-gray-700">
                The full details for this finding couldn't be loaded.
              </p>
              <p className="text-sm text-gray-400">
                Everything above is from the finding list and is up to date.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetryDetail}
              >
                Try again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Findings card — built from vuln.subFindings. The BE refactor
            (Vulnerability sub-findings) made this the primary container
            for what was wrong. Each sub-finding is a self-contained
            block: title + per-finding risk badge + description + any
            populated optional fields (affectedRecord, expected, actual,
            evidence, referenceLink). Per-finding stepsToReproduce /
            recommendation are suppressed when the parent already
            carries them — single-finding pentesters auto-derive both
            into the sub-finding for back-compat, so rendering both
            would duplicate. The legacy 2-column details grid in the
            Information card above takes over for pre-refactor records
            where subFindings is empty. */}
        {hasSubFindings && (
          <Card>
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">Findings</h2>
              <p className="mt-1 text-xs text-gray-400">
                {vuln.subFindings.length} observation{vuln.subFindings.length === 1 ? "" : "s"}
              </p>
            </div>
            <CardContent className="p-6 flex flex-col gap-6">
              {vuln.subFindings.map((subFinding, subFindingIndex) => {
                const findingRiskStyle = getSeverityPillStyle(subFinding.riskLevel);
                const findingRiskLabel = getSeverityLabel(subFinding.riskLevel);
                // Per-finding steps / recommendation only render when
                // the parent doesn't already carry them — avoids the
                // duplicate render on auto-derived single-finding
                // vulns (XSS, SQLi, etc.).
                const showFindingSteps =
                  !!subFinding.stepsToReproduce && !hasSteps;
                const showFindingRecommendation =
                  !!subFinding.recommendation && !hasRecommendations;
                // Numbering is only meaningful when there's more than
                // one finding. For a single-finding vuln the "1." is
                // visual noise.
                const showNumbering = vuln.subFindings.length > 1;

                return (
                  <div
                    key={`finding-${subFindingIndex}`}
                    className={`${subFindingIndex > 0 ? "pt-6 border-t border-gray-100" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                      <h3 className="text-base font-semibold text-gray-900">
                        {showNumbering && (
                          <span className="text-gray-400 mr-2 tabular-nums">
                            {subFindingIndex + 1}.
                          </span>
                        )}
                        {subFinding.title}
                      </h3>
                      <span
                        className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${findingRiskStyle.bg} ${findingRiskStyle.text}`}
                      >
                        {findingRiskLabel}
                      </span>
                    </div>

                    {/* Description — BE preserves newlines, so
                        whitespace-pre-line keeps the structure the
                        pentester intended without forcing rich
                        markup. */}
                    {subFinding.description && (
                      <p className="text-sm text-gray-700 whitespace-pre-line">
                        {subFinding.description}
                      </p>
                    )}

                    {/* Optional fields — dt/dd rows rendered inline
                        as a 1-column stack so multi-line values
                        (Evidence, Expected/Actual) don't fight a
                        2-column grid like the parent details
                        used to. */}
                    {(subFinding.affectedRecord ||
                      subFinding.expected ||
                      subFinding.actual ||
                      subFinding.evidence ||
                      subFinding.referenceLink) && (
                      <dl className="mt-3 flex flex-col gap-2 text-sm">
                        {subFinding.affectedRecord && (
                          <div>
                            <dt className="text-gray-400 text-xs mb-0.5">Affected</dt>
                            <dd className="text-gray-700 break-all">
                              {subFinding.affectedRecord}
                            </dd>
                          </div>
                        )}
                        {subFinding.expected && (
                          <div>
                            <dt className="text-gray-400 text-xs mb-0.5">Expected</dt>
                            <dd className="text-gray-700 whitespace-pre-line">
                              {subFinding.expected}
                            </dd>
                          </div>
                        )}
                        {subFinding.actual && (
                          <div>
                            <dt className="text-gray-400 text-xs mb-0.5">Actual</dt>
                            <dd className="text-gray-700 whitespace-pre-line">
                              {subFinding.actual}
                            </dd>
                          </div>
                        )}
                        {subFinding.evidence && (
                          <div>
                            <dt className="text-gray-400 text-xs mb-0.5">Evidence</dt>
                            <dd className="text-gray-700 whitespace-pre-line break-all rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-xs">
                              {subFinding.evidence}
                            </dd>
                          </div>
                        )}
                        {subFinding.referenceLink && (
                          <div>
                            <dt className="text-gray-400 text-xs mb-0.5">Reference</dt>
                            <dd>
                              {referenceLinkIsNavigable(
                                subFinding.referenceLink,
                                subFinding.referenceLinkNavigable,
                              ) ? (
                                <a
                                  href={subFinding.referenceLink}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline break-all"
                                >
                                  {subFinding.referenceLink}
                                </a>
                              ) : (
                                // Non-http(s) reference (e.g. a discovered
                                // javascript:/data: payload) — render the raw
                                // value as inert text so it's preserved as
                                // evidence but can never execute on click.
                                // See SECURITY_AUDIT_FINDINGS F1.
                                <span
                                  className="text-sm text-gray-700 break-all"
                                  title="Unsafe URL scheme — shown as text, not linked"
                                >
                                  {subFinding.referenceLink}
                                </span>
                              )}
                            </dd>
                          </div>
                        )}
                      </dl>
                    )}

                    {/* Observed HTTP exchanges — the customer-facing
                        evidence card block. Populated for HTTP-based
                        findings (SQLi, XSS, CSRF, Open Redirect, direct
                        RCE/SSRF/XXE). Null/empty on non-HTTP findings
                        (TLS, DNS, InfoDisc) — skip the block entirely
                        in that case rather than render an empty header. */}
                    {Array.isArray(subFinding.httpExchanges) &&
                      subFinding.httpExchanges.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center gap-1.5">
                              <p className="text-xs text-gray-400">Observed HTTP exchanges</p>
                              <HelpTip
                                label="How to read these exchanges"
                                content="The request outlined in red is the one that demonstrated the vulnerability. The others are context — a baseline request, a control, and any discovery probes. Response bodies are truncated to 2 KB."
                                maxWidthPx={270}
                              />
                            </span>
                            <p className="text-[11px] text-gray-400">
                              Response bodies truncated to 2 KB · headers reduced to stack-identifying subset
                            </p>
                          </div>
                          <div className="space-y-3">
                            {subFinding.httpExchanges.map((exchange, exchangeIndex) => (
                              <HttpExchangeCard
                                key={`exchange-${subFindingIndex}-${exchangeIndex}`}
                                exchange={exchange}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                    {showFindingSteps && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-400 mb-0.5">Steps to reproduce</p>
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {subFinding.stepsToReproduce}
                        </p>
                      </div>
                    )}

                    {showFindingRecommendation && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-400 mb-0.5">Recommendation</p>
                        <p className="text-sm text-gray-700 whitespace-pre-line">
                          {subFinding.recommendation}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Steps to Reproduce — screenshot(s) at the top, numbered list
            below. Screenshot and steps live in the same card because the
            screenshot is contextual evidence for *how* to reproduce the
            vuln, not a standalone artifact.

            Withheld while the detail is loading: its empty state makes a
            claim about the finding ("No reproduction steps recorded")
            that we can't stand behind until the record has arrived. */}
        {showDetailCards && (
        <Card>
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Steps to Reproduce</h2>
          </div>
          <CardContent className="p-6 flex flex-col gap-5">
            {hasScreenshot && (
              <div className="rounded-lg border border-gray-200 overflow-hidden">
                <EvidenceImage
                  src={vuln.render}
                  alt="Vulnerability screenshot"
                  className="w-full h-auto block"
                  size="large"
                  canRequestFreshUrl={!hasSpentEvidenceRefresh}
                  onFreshUrlRequested={handleEvidenceUrlExpired}
                  freshUrlAttemptCount={evidenceRefreshAttempts}
                />
              </div>
            )}

            {/* Additional context screenshots (e.g. XSSPage's Vulnerable Input
                and Submit Button shots). Rendered as a labelled row below
                the main render. The composer's step prose refers to these
                by their `label`, so keep the label under each thumbnail. */}
            {Array.isArray(vuln.additionalRenders) && vuln.additionalRenders.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {vuln.additionalRenders.map((render, renderIndex) => (
                  <div
                    key={`${render.label}-${renderIndex}`}
                    className="rounded-lg border border-gray-200 overflow-hidden bg-white"
                  >
                    <EvidenceImage
                      src={render.url}
                      alt={render.label}
                      className="w-full h-auto block"
                      size="small"
                      canRequestFreshUrl={!hasSpentEvidenceRefresh}
                      onFreshUrlRequested={handleEvidenceUrlExpired}
                      freshUrlAttemptCount={evidenceRefreshAttempts}
                    />
                    <div className="px-3 py-2 border-t border-gray-100 text-xs font-medium text-gray-600">
                      {render.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {hasSteps ? (
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                {vuln.stepsToReproduce.map((step, stepIndex) => (
                  <li key={stepIndex} className="leading-relaxed whitespace-pre-line">
                    {step}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-gray-400 text-center py-2">
                No reproduction steps recorded for this vulnerability.
              </p>
            )}
          </CardContent>
        </Card>
        )}

        {/* Recommendations — always rendered with empty state, and
            withheld while loading for the same reason as the card
            above. */}
        {showDetailCards && (
        <Card>
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-900">Recommendations</h2>
          </div>
          <CardContent className="p-6">
            {hasRecommendations ? (
              <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                {realRecommendations.map((recommendation, recommendationIndex) => (
                  <li key={recommendationIndex} className="leading-relaxed">
                    {recommendation}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-gray-400 text-center py-2">
                No recommendations recorded for this vulnerability.
              </p>
            )}
          </CardContent>
        </Card>
        )}

      </div>
    </div>
  );
}

export default withRouter(Vulnerability);
